import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  Input,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { of } from 'rxjs';
import { publicMethods } from '../../../globals';
import { ZintAutocompleteComponent } from '../../../app/html-snippet-components/zint-autocomplete/zint-autocomplete.component';
import { concatMap, filter, tap } from 'rxjs/operators';
import { MessageService } from '../../../services/message.service';

interface ImportMapping {
  customColumn: Record<string, any>;
  crmField: Record<string, any>;
  id?: number | string;
}
@Component({
    selector: 'wizard-crm-import-mapping',
    templateUrl: './wizard-crm-import-mapping.component.html',
    styleUrl: './wizard-crm-import-mapping.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WizardCrmImportMappingComponent {
  @ViewChildren(ZintAutocompleteComponent)
  autocompletes!: QueryList<ZintAutocompleteComponent>;

  @Input() stepData: Record<string, any>;

  crmService = inject(CRMIntegrationsService);
  pubMethods = inject(publicMethods);
  messageService = inject(MessageService);
  snackbar = inject(MatSnackBar);
  injector = inject(Injector);

  CRM_OBJECT_TYPE = 'crm_object_type';
  LIST_CRM_FIELDS_OPTIONS = 'list_crm_object_fields';
  LIST_CUSTOM_COLUMNS = 'list_custom_columns';
  IMPORT_MAPPINGS = 'import_mappings';
  CRM_OBJECT_FIELDS = 'crm_object_fields';

  crmType: string = '';
  stepType: string = '';
  crmObjectID = computed(() => {
    return this.crmService.configCRMSelectedObjectId();
  });

  isImportEnabled = false;

  crmProperties = [];

  customColumns = [];

  selectedImportMappings: ImportMapping[] = [];
  currentCRMFieldType: string = '';

  defaultMapping: ImportMapping = {
    crmField: {},
    customColumn: {},
  };

  ngOnInit() {
    this.crmType = this.crmService.configCRMType();
    this.stepType = this.stepData.stepType;
    this.getStepActionContent();

    this.selectedImportMappings.push({ ...this.defaultMapping });
  }

  getStepActionContent(): void {
    this.crmService
      .getCRMExistingState({ actionType: this.CRM_OBJECT_TYPE })
      .subscribe(data => {
        if (this.pubMethods.hasData(data)) {
          if (!this.crmObjectID()) {
            this.crmService.configCRMSelectedObjectId.update(() => data[0].id);
          }
          this.isImportEnabled = data[0].importEnabled;

          this.getExistingImportMappings();
        }
      });
  }

  getExistingImportMappings(): void {
    this.crmService
      .getCRMExistingState({
        actionType: this.IMPORT_MAPPINGS,
        objectId: this.crmObjectID(),
      })
      .pipe(
        tap(data => {
          if (this.pubMethods.hasData(data)) {
            data.forEach(mapping => {
              const { id, crmField, customColumn } = mapping;
              this.selectedImportMappings = [
                ...this.selectedImportMappings,
                { id, crmField, customColumn },
              ];
            });
          }
        }),
        filter(() => this.isImportEnabled),
        tap(() => {
          this.fetchCRMFields();
        })
      )
      .subscribe();
  }

  fetchCRMFields(): void {
    /**
     * extracted method so it can used as part of getExistingImportMappings chain
     * and manual refresh
     */
    this.crmService
      .getCRMStepActionContent({
        stepType: this.stepType,
        actionType: this.LIST_CRM_FIELDS_OPTIONS,
        objectId: this.crmObjectID(),
        refetch: true,
      })
      .subscribe(crmFieldsData => {
        this.crmProperties = crmFieldsData;
      });
  }

  getCustomColsFromCRMFieldType(
    selectedCRMField: Record<string, any>,
    selectedIndex?: number
  ): void {
    this.updateSelectedPropertyAtIndex(
      selectedIndex,
      'crmField',
      selectedCRMField
    );
    const { fieldType } = selectedCRMField;

    if (this.currentCRMFieldType === fieldType) {
      return;
    }
    this.currentCRMFieldType = fieldType;
    const queryParamToAppend = `?crm_field_type=${fieldType || 'String'}`;

    this.crmService
      .getCRMStepActionContent({
        stepType: this.stepType,
        actionType: this.LIST_CUSTOM_COLUMNS,
        objectId: this.crmObjectID(),
        qParam: queryParamToAppend,
        refetch: true,
      })
      .subscribe(data => {
        this.customColumns = data;
      });
  }

  confirmMappingAndAddMore(mapIndex: number): void {
    const foundMapping = this.selectedImportMappings[mapIndex];

    const areFieldsValid =
      this.pubMethods.hasData(foundMapping.crmField) &&
      this.pubMethods.hasData(foundMapping.customColumn);

    if (!areFieldsValid) {
      this.snackbar.open('Select both properties', 'X', {
        duration: 2200,
      });
      return;
    }

    const reqBodyForCrmField = { ...foundMapping.crmField, lookupField: null };
    let fieldIdToPostCustomColumn = null;

    // POST crmField to get fieldID 1st
    this.crmService
      .postCRMStepActionJSON({
        postActionType: this.CRM_OBJECT_FIELDS,
        objectId: this.crmObjectID(),
        params: reqBodyForCrmField,
      })
      .pipe(
        tap(response => {
          // get the fieldID for the following POST call
          if (response.ok) {
            fieldIdToPostCustomColumn = response.body.id;
            this.selectedImportMappings[mapIndex].crmField.id =
              response.body.id;
          } else {
            const errorDetails = response.error?.error || response.body;
            const errMsg = Object.values(errorDetails).flat().join('. ');
            if (errMsg) {
              this.messageService.show(errMsg);
            } else {
              console.warn('something went wrong', response);
            }
          }
        }),
        filter(response => response.ok), //if error stop 2nd call
        concatMap(() => {
          // POST again to import_mapping to complete
          const {
            customColumn: { id: customColumnId },
          } = foundMapping;

          const reqBodyForCustomColumnPost = {
            crmFieldId: fieldIdToPostCustomColumn,
            customColumnId,
          };
          return this.crmService.postCRMStepActionJSON({
            postActionType: this.IMPORT_MAPPINGS,
            objectId: this.crmObjectID(),
            params: reqBodyForCustomColumnPost,
          });
        }),
        tap(response => {
          if (response.ok) {
            this.addMappingsSelectionRow();
            this.resetAllMappingSelects();
            this.crmService.getCRMConfigStepStatus(this.stepData.stepType);
          } else {
            const errorDetails = response.error?.error || response.body;
            const errMsg = Object.values(errorDetails).flat().join('. ');
            if (errMsg) {
              this.messageService.show(errMsg);
            }
          }
        })
      )
      .subscribe();
  }

  updateSelectedPropertyAtIndex(
    index: number,
    fieldName: keyof ImportMapping,
    data: Record<string, any>
  ) {
    this.selectedImportMappings[index][fieldName as string] = data;
  }

  addMappingsSelectionRow(): void {
    this.selectedImportMappings.unshift({
      ...this.defaultMapping,
    });
  }

  handleToggleImportMappings(): void {
    const reqBody = { importEnabled: this.isImportEnabled };

    this.crmService
      .updateCRMStepActionContent({
        updateActionType: this.CRM_OBJECT_TYPE,
        objectId: this.crmObjectID(),
        updateMethod: 'patch',
        params: reqBody,
      })
      .pipe(
        tap(response => {
          if (!this.isImportEnabled && response.ok) {
            this.crmProperties = [];
            this.snackbar.open('Import has been disabled.', 'OK', {
              duration: 2200,
            });
          }
        }),
        concatMap(response => {
          if (this.isImportEnabled && response.ok) {
            return this.crmService.getCRMStepActionContent({
              stepType: this.stepType,
              actionType: this.LIST_CRM_FIELDS_OPTIONS,
              objectId: this.crmObjectID(),
              refetch: true,
            });
          } else {
            return of([]);
          }
        }),
        tap(crmFieldsData => {
          this.crmProperties = crmFieldsData;
        })
      )
      .subscribe();
  }

  resetAllMappingSelects(): void {
    this.autocompletes.forEach(component => component.reset());
  }

  resetFieldsMappings(): void {
    this.selectedImportMappings = [this.defaultMapping];
  }

  removeMapping(mapIndex: number): void {
    /**
     * DELETE /crm_framework/crms/hubspot/object/{crmObjectPk}/field/{id}/
     */
    const mappingToDelete = this.selectedImportMappings[mapIndex];
    if (!mappingToDelete) return;
    const {
      crmField: { id },
    } = mappingToDelete;
    this.crmService
      .updateCRMStepActionContent({
        updateActionType: this.CRM_OBJECT_FIELDS,
        objectId: this.crmObjectID(),
        fieldId: Number(id),
        updateMethod: 'delete',
      })
      .subscribe(response => {
        if (response.ok) {
          this.selectedImportMappings = this.selectedImportMappings.filter(
            (_, index) => index !== mapIndex
          );
        }
      });
  }
}
